import { useMemo, useState, type FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { getEventoSlug } from '@/lib/eventos'
import { useOpenGraph } from '@/hooks/useOpenGraph'
import type {
  EventoDetalhe,
  PagamentoEditorState,
  ParticipanteEvento,
  StatusInscricao,
} from './types'
import { calcularEstatisticas } from './utils'
import { PaymentModal } from './components/payment-modal'
import { ParticipantsSection } from './components/participants-section'
import { FinanceSummaryCard } from './components/finance-summary-card'
import { EventStatsCard } from './components/event-stats-card'
import { EventOverviewCard } from './components/event-overview-card'
import { EventDetailHeader } from './components/event-detail-header'
import { EquipeEventoCard } from './components/equipe-evento-card'
import { useAuth } from '@/hooks/use-auth-wrapper'
import { useEvento, useEventoEquipe } from '@/hooks/use-eventos'
import {
  useParticipantes,
  useEstatisticasEvento,
  useAtualizarStatusParticipante,
  useConfirmarPagamento
} from '@/hooks/use-participantes'
import type { ParticipanteComRelacoes } from '@/services/participantes.service'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'

import { exportParticipantsToPdf } from './exporters'

export function EventoDetalhePage() {
  const { eventoSlug } = useParams<{ eventoSlug: string }>()
  const navigate = useNavigate()
  const { perfil } = useAuth()

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const [paymentEditor, setPaymentEditor] = useState<PagamentoEditorState>()

  // Buscar evento do banco de dados
  const { data: eventoData, isLoading: isLoadingEvento } = useEvento(eventoSlug)
  const { data: equipeData, isLoading: isLoadingEquipe } = useEventoEquipe(eventoData?.id)

  // Buscar participantes e estatísticas do banco
  const { data: participantesData, isLoading: isLoadingParticipantes } = useParticipantes(eventoData?.id)
  const { data: estatisticasData, isLoading: isLoadingEstatisticas } = useEstatisticasEvento(eventoData?.id)

  // Mutations para atualizar dados
  const atualizarStatus = useAtualizarStatusParticipante()
  const confirmarPagamento = useConfirmarPagamento()

  // Combinar dados do evento com a equipe
  const evento = useMemo<EventoDetalhe | null>(() => {
    if (!eventoData) return null

    return {
      ...eventoData,
      equipe: equipeData || [],
    } as EventoDetalhe
  }, [eventoData, equipeData])

  // Mapear participantes do banco para o formato esperado
  const participantes = useMemo<ParticipanteEvento[]>(() => {
    if (!participantesData) return []

    return participantesData.map((p: ParticipanteComRelacoes) => {
      const status = (p.status ?? 'pendente') as StatusInscricao
      const funcoes = p.funcoes ?? []
      const pagamentos = Array.isArray(p.pagamento)
        ? p.pagamento
        : p.pagamento
          ? [p.pagamento]
          : []
      const parcelas = Array.isArray((p as any).parcelas)
        ? (p as any).parcelas
        : (p as any).parcelas
          ? [(p as any).parcelas]
          : []
      const checkins = Array.isArray(p.checkin) ? p.checkin : p.checkin ? [p.checkin] : []
      const pagamentoAtual = pagamentos[0]
      const checkinAtual = checkins[0]

      // Calcular status de pagamento considerando parcelas
      let statusPagamento = pagamentoAtual?.status ?? 'pendente'
      let valorTotal = pagamentoAtual?.valor ?? 0
      let metodoPagamento = pagamentoAtual?.metodo
      let dataPagamento = pagamentoAtual?.pago_em

      // Se há parcelas, calcular status baseado nelas
      let parcelasPagas = 0
      let parcelasTotal = 0

      if (parcelas.length > 0) {
        const totalParcelas = parcelas.reduce((sum: number, par: any) => sum + Number(par.valor), 0)
        const parcelasPagasArray = parcelas.filter((par: any) => par.status === 'pago')
        
        valorTotal = totalParcelas
        parcelasPagas = parcelasPagasArray.length
        parcelasTotal = parcelas.length

        if (parcelasPagasArray.length === parcelas.length) {
          statusPagamento = 'pago'
        } else {
          statusPagamento = 'pendente'
        }

        // Usar método e data da última parcela paga
        if (parcelasPagasArray.length > 0) {
          const ultimaParcela = parcelasPagasArray[parcelasPagasArray.length - 1]
          metodoPagamento = ultimaParcela.metodo
          dataPagamento = ultimaParcela.pago_em
        }
      }

      return {
        id: p.id,
        nome: p.nome,
        email: p.email ?? '',
        telefone: p.telefone ?? '',
        status,
        funcoes,
        pagamento: {
          status: statusPagamento,
          metodo: metodoPagamento ?? undefined,
          valor: valorTotal,
          pagoEm: dataPagamento ?? undefined,
          parcelasPagas: parcelasTotal > 0 ? parcelasPagas : undefined,
          parcelasTotal: parcelasTotal > 0 ? parcelasTotal : undefined,
        },
        checkIn: {
          status: checkinAtual?.status === 'realizado' ? 'realizado' : 'pendente',
          horario: checkinAtual?.horario ?? undefined,
        },
        comprovanteUrl: p.comprovante_url ?? undefined,
      }
    })
  }, [participantesData])

  // Usar estatísticas do banco
  const stats = useMemo(() => {
    if (!estatisticasData || !evento) {
      return calcularEstatisticas([], evento)
    }

    return {
      total: estatisticasData.total,
      confirmados: estatisticasData.confirmados,
      pendentes: estatisticasData.pendentes,
      cancelados: estatisticasData.cancelados,
      ocupacao: evento.capacidade ? (estatisticasData.total / evento.capacidade) * 100 : null,
      receitaPrevista: estatisticasData.receitaPrevista,
      receitaRecebida: estatisticasData.receitaRecebida,
      receitaPendente: estatisticasData.receitaPendente,
      taxaConfirmacao: estatisticasData.taxaConfirmacao,
    }
  }, [estatisticasData, evento])

  const pagamentosPorMetodo = useMemo(() => {
    return estatisticasData?.pagamentosPorMetodo || {}
  }, [estatisticasData])

  // Configurar meta tags para compartilhamento social
  const { getShareData } = useOpenGraph(
    evento
      ? {
          title: evento.titulo,
          description: evento.descricao || `Participe do evento ${evento.titulo}!`,
          type: 'event',
        }
      : undefined
  )

  const permissoes = useMemo(() => {
    const userPermissions = {
      editar_evento: false,
      excluir_evento: false,
      confirmar_pagamento: false,
    }
    if (!perfil || !evento?.equipe) return userPermissions

    const membroDaEquipe = evento.equipe.find((m) => m.id === perfil.id)
    if (membroDaEquipe) {
      return membroDaEquipe.permissoes
    }

    return userPermissions
  }, [perfil, evento])

  const participanteEmEdicao = useMemo(() => {
    if (!paymentEditor) return null
    return (
      participantes.find((participante) => participante.id === paymentEditor.participantId) ?? null
    )
  }, [paymentEditor, participantes])

  const handleStatusChange = async (id: string, status: StatusInscricao) => {
    try {
      await atualizarStatus.mutateAsync({ id, status })
    } catch (error) {
      console.error('Erro ao atualizar status:', error)
    }
  }

  const handleOpenPayment = (participante: ParticipanteEvento) => {
    setPaymentEditor({
      participantId: participante.id,
      valor: participante.pagamento.valor.toString(),
      metodo: participante.pagamento.metodo ?? 'pix',
    })
  }

  const handleClosePayment = () => setPaymentEditor(undefined)

  const handlePaymentChange = (state: PagamentoEditorState) => setPaymentEditor(state)

  const handleSubmitPayment = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!paymentEditor || !evento || !perfil) return

    const participanteAtual = participantes.find((p) => p.id === paymentEditor.participantId)
    if (!participanteAtual) return

    const valorNovo =
      paymentEditor.valor.trim() === ''
        ? participanteAtual.pagamento.valor
        : Number(paymentEditor.valor)

    if (Number.isNaN(valorNovo)) return

    try {
      await confirmarPagamento.mutateAsync({
        participanteId: paymentEditor.participantId,
        eventoId: evento.id,
        metodo: paymentEditor.metodo,
        valor: valorNovo,
        registradoPor: perfil.id,
      })
      handleClosePayment()
    } catch (error) {
      console.error('Erro ao confirmar pagamento:', error)
    }
  }

  const handleShareLink = () => {
    if (!evento) return
    const link = `${window.location.origin}/eventos/${getEventoSlug(evento)}/inscricao`

    // Usar dados de compartilhamento otimizados com informações da igreja
    const shareData = getShareData({
      title: evento.titulo,
      description: `Participe do evento ${evento.titulo}!`,
      url: link,
    })

    if (navigator.share) {
      navigator.share(shareData).catch(() => {
        navigator.clipboard.writeText(link).then(() => {
          setCopied(true)
          setTimeout(() => setCopied(false), 2000)
        })
      })
    } else {
      navigator.clipboard.writeText(link).then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      })
    }
  }

  const handleExportParticipants = async () => {
    if (!evento || participantes.length === 0) return
    await exportParticipantsToPdf(evento, participantes, stats)
  }

  const handleAddParticipant = () => {
    const slug = eventoSlug ?? (evento ? getEventoSlug(evento) : '')
    if (slug) {
      navigate(`/eventos/${slug}/participantes/novo`)
    }
  }

  const handleEdit = () => {
    if (evento) {
      navigate(`/eventos/${eventoSlug}/editar`)
    }
  }

  const handleDelete = () => {
    setIsDeleteModalOpen(true)
  }

  const confirmDelete = () => {
    console.log('Evento excluído:', evento?.id)
    setIsDeleteModalOpen(false)
    navigate('/eventos')
  }

  // Loading state
  if (isLoadingEvento || isLoadingEquipe || isLoadingParticipantes || isLoadingEstatisticas) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center gap-2">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <span>Carregando evento...</span>
        </div>
      </div>
    )
  }

  // Not found state
  if (!evento) {
    return (
      <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-6 lg:py-8 xl:px-8">
        <p className="text-muted-foreground">Evento não encontrado.</p>
      </div>
    )
  }

  const isEventoGratuito = evento.gratuito === true

  return (
    <>
      <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-6 lg:py-8 xl:px-8">
        <EventDetailHeader
          evento={evento}
          permissoes={permissoes}
          copied={copied}
          onShare={handleShareLink}
          onExport={handleExportParticipants}
          onAddParticipant={handleAddParticipant}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="grid gap-6 lg:grid-cols-3"
        >
          <div className="space-y-6 lg:col-span-2">
            <EventOverviewCard evento={evento} stats={stats} />
            <ParticipantsSection
              participantes={participantes}
              onStatusChange={handleStatusChange}
              onOpenPayment={handleOpenPayment}
              onOpenCarne={(participante) => navigate(`/eventos/${eventoSlug}/participantes/${participante.id}/carne`)}
              eventoGratuito={isEventoGratuito}
              eventoPermiteParcelamento={evento?.permitir_parcelamento || false}
            />
          </div>
          <div className="space-y-6">
            <EventStatsCard stats={stats} />
            {!isEventoGratuito && (
              <FinanceSummaryCard stats={stats} pagamentosPorMetodo={pagamentosPorMetodo} evento={evento} />
            )}
            {evento.equipe && <EquipeEventoCard equipe={evento.equipe} />}
          </div>
        </motion.div>
      </div>

      <PaymentModal
        editor={paymentEditor}
        participante={participanteEmEdicao}
        onClose={handleClosePayment}
        onChange={handlePaymentChange}
        onSubmit={handleSubmitPayment}
      />

      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir o evento "{evento.titulo}"? Essa ação não pode ser
              desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteModalOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
